import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@jsq/ds/core";
import { useGetBillingExportLazyQuery } from "~/graphql/supergraphHooks";

const POLLING_INTERVAL = 1000;

type UseBillingExportPollingParams = {
  exportId: string | null;
  onJobStatusChange?: (inProgress: boolean) => void;
  closeModal: () => void;
  enabled: boolean;
};

export function useBillingExportPolling({
  exportId,
  onJobStatusChange,
  closeModal,
  enabled,
}: UseBillingExportPollingParams) {
  const [isPolling, setIsPolling] = useState(false);

  const [getBillingExportStatus, { error: queryError }] = useGetBillingExportLazyQuery({
    fetchPolicy: "network-only",
  });

  const savedOnJobStatusChange = useRef(onJobStatusChange);
  const savedCloseModal = useRef(closeModal);

  useEffect(() => {
    savedOnJobStatusChange.current = onJobStatusChange;
    savedCloseModal.current = closeModal;
  }, [onJobStatusChange, closeModal]);

  const pollExportStatus = useCallback(async () => {
    const { data, error } = await getBillingExportStatus({
      variables: { exportId: exportId ?? "" },
    });

    if (error) {
      toast.error(error.message);
      setIsPolling(false);
      return;
    }

    const exportData = data?.getBillingExport;

    if (!exportData) {
      toast.error("Error fetching export status: No data returned.");
      setIsPolling(false);
      return;
    }

    switch (exportData.status) {
      case "ready":
        if (exportData.s3Path) {
          toast.success("Export generated successfully.");
          window.open(exportData.s3Path, "_blank");
        } else {
          toast.error("Export ready but download link is missing.");
        }
        setIsPolling(false);
        break;
      case "error":
        toast.error(`Export failed: ${exportData.errorMessage ?? "Unknown error"}`);
        setIsPolling(false);
        break;
      case "pending":
        // Polling continues, do nothing to change isPolling state
        break;
      default:
        toast.error(`Unexpected export status: ${exportData.status as string}`);
        setIsPolling(false);
        break;
    }
  }, [exportId, getBillingExportStatus]);

  useEffect(() => {
    if (enabled && exportId) {
      setIsPolling(true);
    } else {
      setIsPolling(false);
    }
  }, [enabled, exportId]);

  useEffect(() => {
    if (isPolling) {
      savedOnJobStatusChange.current?.(true);
      const intervalId = setInterval(pollExportStatus, POLLING_INTERVAL);
      void pollExportStatus();

      return () => {
        clearInterval(intervalId);
        savedOnJobStatusChange.current?.(false);
        savedCloseModal.current();
      };
    }
  }, [isPolling, pollExportStatus]);

  useEffect(() => {
    if (queryError) {
      toast.error(queryError.message);
      setIsPolling(false);
    }
  }, [queryError]);

  return {
    isPollingInProgress: isPolling || (enabled && !exportId),
  };
}
